import React, { useState, useEffect } from 'react';
import { ref, get, set, push, remove } from 'firebase/database';
import { database, db } from '../firebase';
import { collection, getDocs } from 'firebase/firestore';

const NotificationAdminPanel = () => {
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [newNotification, setNewNotification] = useState({
        title: '',
        message: '',
        type: 'info',
        targetUsers: 'all'
    });
    const [globalMessage, setGlobalMessage] = useState('');
    const [savingGlobal, setSavingGlobal] = useState(false);

    useEffect(() => {
        loadNotifications();
        loadGlobalMessage();
    }, []);

    const loadGlobalMessage = async () => {
        try {
            const msgRef = ref(database, 'notifications/app_message');
            const snap = await get(msgRef);
            if (snap.exists()) {
                setGlobalMessage(snap.val());
            }
        } catch (e) {
            console.error("Error loading global message:", e);
        }
    };

    const saveGlobalMessage = async () => {
        setSavingGlobal(true);
        try {
            const msgRef = ref(database, 'notifications/app_message');
            await set(msgRef, globalMessage);
            alert('Mensaje global actualizado correctamente');
        } catch (e) {
            alert('Error al guardar mensaje: ' + e.message);
        } finally {
            setSavingGlobal(false);
        }
    };

    const loadNotifications = async () => {
        try {
            const notificationsRef = ref(database, 'notifications/admin_history');
            const snapshot = await get(notificationsRef);

            if (snapshot.exists()) {
                const data = snapshot.val();
                const notificationsArray = Object.entries(data).map(([id, notification]) => ({
                    id,
                    ...notification
                }));
                setNotifications(notificationsArray.reverse());
            } else {
                setNotifications([]);
            }
        } catch (error) {
            console.error('Error cargando notificaciones:', error);
        } finally {
            setLoading(false);
        }
    };

    const sendNotification = async () => {
        if (!newNotification.title || !newNotification.message) {
            alert('Por favor completa título y mensaje');
            return;
        }

        try {
            const notificationsRef = ref(database, 'notifications/admin_history');
            const newNotificationRef = push(notificationsRef);

            const notificationData = {
                ...newNotification,
                id: newNotificationRef.key,
                timestamp: Date.now(),
                sentBy: 'admin',
                status: 'sent'
            };

            await set(newNotificationRef, notificationData);

            // También enviar a notificaciones de usuarios
            await sendToUsers(notificationData);

            setNewNotification({
                title: '',
                message: '',
                type: 'info',
                targetUsers: 'all'
            });

            loadNotifications();
            alert('Notificación enviada exitosamente');
        } catch (error) {
            console.error('Error enviando notificación:', error);
            alert('Error al enviar la notificación');
        }
    };

    const sendToUsers = async (notification) => {
        try {
            // Usar Firestore para obtener usuarios de manera más eficiente
            const usersSnapshot = await getDocs(collection(db, 'users'));

            if (!usersSnapshot.empty) {
                for (const userDoc of usersSnapshot.docs) {
                    const userId = userDoc.id;
                    const userNotificationsRef = ref(database, `notifications/usersId/${userId}`);
                    const newUserNotificationRef = push(userNotificationsRef);

                    await set(newUserNotificationRef, {
                        id: newUserNotificationRef.key,
                        type: 'admin',
                        title: notification.title,
                        message: notification.message,
                        timestamp: notification.timestamp,
                        read: false,
                        priority: 'high'
                    });
                }
            }
        } catch (error) {
            console.error('Error enviando notificaciones a usuarios:', error);
        }
    };

    const deleteNotification = async (notificationId) => {
        if (!confirm('¿Estás seguro de que quieres eliminar esta notificación?')) {
            return;
        }

        try {
            const notificationRef = ref(database, `notifications/admin_history/${notificationId}`);
            await remove(notificationRef);
            loadNotifications();
        } catch (error) {
            console.error('Error eliminando notificación:', error);
            alert('Error al eliminar la notificación');
        }
    };

    const getTypeIcon = (type) => {
        switch (type) {
            case 'success': return '✅';
            case 'warning': return '⚠️';
            case 'error': return '❌';
            case 'info':
            default: return 'ℹ️';
        }
    };

    const getTypeColor = (type) => {
        switch (type) {
            case 'success': return '#10b981';
            case 'warning': return '#f59e0b';
            case 'error': return '#ef4444';
            case 'info':
            default: return '#3b82f6';
        }
    };

    if (loading) {
        return <div className="loading">Cargando notificaciones...</div>;
    }

    return (
        <div className="notification-admin-panel">
            <div className="panel-section" style={{ borderBottom: '2px solid rgba(255,255,255,0.05)', paddingBottom: '30px', marginBottom: '30px' }}>
                <h3 style={{ color: 'var(--accent)' }}>📢 Mensaje de Bienvenida Global</h3>
                <p style={{ opacity: 0.6, fontSize: '0.8rem', marginBottom: '15px' }}>Este mensaje aparece en la pantalla de inicio de TODOS los usuarios (Android y Web).</p>
                <div style={{ display: 'flex', gap: '10px' }}>
                    <input
                        type="text"
                        value={globalMessage}
                        onChange={(e) => setGlobalMessage(e.target.value)}
                        placeholder="Escribe el mensaje de la app aquí..."
                        style={{ flex: 1, padding: '12px', borderRadius: '8px', border: '1px solid var(--border-color)', background: 'var(--bg-secondary)', color: 'white' }}
                    />
                    <button
                        onClick={saveGlobalMessage}
                        disabled={savingGlobal}
                        style={{ padding: '0 25px', borderRadius: '8px', background: 'var(--accent)', color: 'white', border: 'none', cursor: 'pointer', fontWeight: 'bold' }}
                    >
                        {savingGlobal ? 'Guardando...' : 'Actualizar'}
                    </button>
                    <button
                        onClick={() => { setGlobalMessage(''); set(ref(database, 'notifications/app_message'), null); }}
                        style={{ padding: '0 15px', borderRadius: '8px', background: '#444', color: 'white', border: 'none', cursor: 'pointer' }}
                    >
                        Quitar
                    </button>
                </div>
            </div>

            <div className="panel-section">
                <h3>Enviar Nueva Notificación Push/Local</h3>
                <div className="notification-form">
                    <div className="form-group">
                        <label>Título:</label>
                        <input
                            type="text"
                            value={newNotification.title}
                            onChange={(e) => setNewNotification({ ...newNotification, title: e.target.value })}
                            placeholder="Título de la notificación"
                        />
                    </div>

                    <div className="form-group">
                        <label>Mensaje:</label>
                        <textarea
                            value={newNotification.message}
                            onChange={(e) => setNewNotification({ ...newNotification, message: e.target.value })}
                            placeholder="Mensaje de la notificación"
                            rows="3"
                        />
                    </div>

                    <div className="form-group">
                        <label>Tipo:</label>
                        <select
                            value={newNotification.type}
                            onChange={(e) => setNewNotification({ ...newNotification, type: e.target.value })}
                        >
                            <option value="info">Información</option>
                            <option value="success">Éxito</option>
                            <option value="warning">Advertencia</option>
                            <option value="error">Error</option>
                        </select>
                    </div>

                    <div className="form-group">
                        <label>Destinatarios:</label>
                        <select
                            value={newNotification.targetUsers}
                            onChange={(e) => setNewNotification({ ...newNotification, targetUsers: e.target.value })}
                        >
                            <option value="all">Todos los usuarios</option>
                            <option value="premium">Solo usuarios premium</option>
                            <option value="free">Solo usuarios gratuitos</option>
                        </select>
                    </div>

                    <button onClick={sendNotification} className="send-notification-btn">
                        📤 Enviar Notificación
                    </button>
                </div>
            </div>

            <div className="panel-section">
                <h3>Notificaciones Enviadas</h3>
                <div className="notifications-list">
                    {notifications.length === 0 ? (
                        <p className="no-notifications">No hay notificaciones enviadas</p>
                    ) : (
                        notifications.map(notification => (
                            <div key={notification.id} className="notification-item">
                                <div className="notification-header">
                                    <div className="notification-type">
                                        <span
                                            className="type-icon"
                                            style={{ color: getTypeColor(notification.type) }}
                                        >
                                            {getTypeIcon(notification.type)}
                                        </span>
                                        <span className="type-label">{notification.type}</span>
                                    </div>
                                    <div className="notification-actions">
                                        <button
                                            onClick={() => deleteNotification(notification.id)}
                                            className="delete-btn"
                                        >
                                            🗑️
                                        </button>
                                    </div>
                                </div>

                                <div className="notification-content">
                                    <h4>{notification.title}</h4>
                                    <p>{notification.message}</p>
                                    <div className="notification-meta">
                                        <span>Destinatarios: {notification.targetUsers}</span>
                                        <span>
                                            {new Date(notification.timestamp).toLocaleString('es-ES')}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>
        </div>
    );
};

export default NotificationAdminPanel;